import { getQueryKey as getMilestonesQueryKey } from "@faire/web-api--source/api/user/milestones/userToken/list/get-hooks";
import { useMutation as useMilestoneMutation } from "@faire/web-api--source/api/user/milestones/userToken/post-hooks";
import { IGetUserMilestonesResponse } from "@faire/web-api--source/indigofair/data/IGetUserMilestonesResponse";
import { IUserMilestone } from "@faire/web-api--source/indigofair/data/IUserMilestone";
import { useQueryClient } from "@tanstack/react-query";
import differenceInDays from "date-fns/differenceInDays";
import { useCallback, useMemo } from "react";

import { getUser } from "../serialized-data/getUser";

import { useMilestonesData } from "./useMilestonesData";

export function useRecordRetailerMilestone(): (
  milestoneType: IUserMilestone.RetailerMilestone
) => Promise<unknown> {
  const userToken = getUser()?.token;
  const { mutateAsync } = useMilestoneMutation();
  const queryClient = useQueryClient();
  return useCallback(
    async (milestoneType: IUserMilestone.RetailerMilestone) => {
      if (!userToken) {
        return Promise.resolve();
      }
      return mutateAsync([
        userToken,
        IUserMilestone.build({ retailer_milestone: milestoneType }),
      ]).then((updatedMilestone) => {
        queryClient.setQueryData<IGetUserMilestonesResponse>(
          getMilestonesQueryKey(userToken),
          (prev) => {
            return IGetUserMilestonesResponse.build({
              milestones: {
                ...prev?.milestones,
                [milestoneType]: updatedMilestone,
              },
            });
          }
        );
      });
    },
    [mutateAsync, queryClient, userToken]
  );
}

export const useRetailerMilestone = (
  milestoneType: IUserMilestone.RetailerMilestone
): {
  hasOccurred: boolean;
  count: number;
  setHasOccurred: () => Promise<unknown>;
  incrementCount: () => Promise<unknown>;
  isLoading: boolean;
} => {
  const { isLoading, milestones } = useMilestonesData();
  const count = milestones?.[milestoneType]?.count || 0;
  const hasOccurred = count > 0;

  const record = useRecordRetailerMilestone();

  const setHasOccurred = useCallback(async () => {
    if (!hasOccurred) {
      record(milestoneType);
    }
    return Promise.resolve();
  }, [hasOccurred, record, milestoneType]);

  const incrementCount = useCallback(
    () => record(milestoneType),
    [record, milestoneType]
  );

  return useMemo(
    () => ({ hasOccurred, count, setHasOccurred, incrementCount, isLoading }),
    [count, hasOccurred, incrementCount, setHasOccurred, isLoading]
  );
};

export function useRecordMilestoneOnce(): (
  milestoneType: IUserMilestone.RetailerMilestone
) => Promise<unknown> {
  const { milestones } = useMilestonesData();
  const record = useRecordRetailerMilestone();

  return useCallback(
    (milestoneType: IUserMilestone.RetailerMilestone) => {
      if (!milestones?.[milestoneType]?.count) {
        return record(milestoneType);
      }
      return Promise.resolve();
    },
    [milestones, record]
  );
}

export function useRecordRetailerMilestoneByDay(): (
  milestoneType: IUserMilestone.RetailerMilestone
) => Promise<unknown> {
  const { milestones } = useMilestonesData();
  const record = useRecordRetailerMilestone();

  return useCallback(
    (milestoneType: IUserMilestone.RetailerMilestone) => {
      const milestone = milestones?.[milestoneType];
      if (!milestone?.count) {
        return record(milestoneType);
      }
      const updatedAt = milestone?.updated_at ?? 0;
      const now = Date.now();
      if (differenceInDays(now, updatedAt) >= 1) {
        return record(milestoneType);
      }
      return Promise.resolve();
    },
    [milestones, record]
  );
}

export function useIsRetailerMilestoneOverWeekOld(
  milestoneType: IUserMilestone.RetailerMilestone
): boolean {
  return useIsRetailerMilestoneOverDaysOld(milestoneType, 7);
}

export function useIsRetailerMilestoneOverDaysOld(
  milestoneType: IUserMilestone.RetailerMilestone,
  days: number
): boolean {
  const milestone = useMilestonesData()?.milestones?.[milestoneType];
  if (!milestone?.count) {
    return false;
  }
  const updatedAt = milestone?.updated_at ?? 0;
  const now = Date.now();
  if (differenceInDays(now, updatedAt) >= days) {
    return true;
  }
  return false;
}
